import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import './src/variables.css';

import { PRIMARY_RAMP, COLOR_SCHEMES, RADIUS } from './brand.mjs';
import { componentTokens, componentTokensByColorScheme } from './componentTokens.mjs';

/**
 * Midnight Mint — PrimeVue preset.
 *
 * The brand lives HERE, in the preset, per PrimeVue's theming guidelines —
 * not as raw `--p-*` CSS-variable overrides. `definePreset` generates the
 * design tokens PrimeVue widgets consume, so a Button, Select, or Toast picks
 * up the mint accent and dark surfaces automatically.
 *
 * This file is the whole brand. The `semantic` block below defines the mint
 * accent ramp and the neutral surface ramps per scheme; the `extend` block adds
 * the brand primitives PrimeVue has no token for (type stacks, the oversized
 * radii, the auction lights, the layout inset), which PrimeVue emits as real
 * `--p-*` variables. Component CSS reads those directly — there is no
 * intermediate aliasing layer, and `./src/variables.css` is down to the single
 * token PrimeVue has no concept of (`--surface-ground`).
 *
 * The mobile app ships locked to dark mode (`<html class="p-dark">` +
 * `darkModeSelector: '.p-dark'`), but both schemes are first-class: hand-rolled
 * component CSS reads the same semantic tokens the widgets do
 * (`--p-content-background`, `--p-text-color`, `--p-primary-color`…), so
 * flipping `.p-dark` re-themes widgets AND custom components together.
 * Storybook exercises both. Keep `light` and `dark` in lockstep — a token
 * defined in one scheme should be defined in the other.
 */
// The state-signal palette, hoisted so it has ONE definition. It is consumed
// twice below: `extend.colorScheme.*.status` emits it as `--p-status-*` for
// hand-written component CSS, and `components.tag.colorScheme` maps the same
// values onto PrimeVue's Tag severities so a `<Tag severity="warn">` and a
// hand-rolled `--p-status-warning` chip cannot drift apart.
//
// The hues are the design system's `--info-*`, `--warn-*`, `--danger-*` and
// `--success-*`. What it does NOT define is an ink to sit on them, because it
// uses those values as text and borders rather than as solid fills — and this
// preset fills them (see `tagSeverities` below). So each scheme takes the
// design system's hue at the stop that clears WCAG AA against the ink it is
// actually paired with, which is not always the stop the design system names:
//
//   dark  — warn and success land as published; info and danger go one stop
//           LIGHTER than the design system names, for the mirror image of the
//           reason light moves three of its own down (below). On the Carbon card
//           (#141414) warn is 8.58:1 and success 10.04:1. The published info
//           (#3b82f6) and danger (#ef4444) clear that card as ink — 5.01:1 and
//           4.90:1 — and then fail the two grounds those hues are actually read
//           on: their OWN 16% tint, where info measures 4.14:1, and
//           `content.hoverBackground` (#1f1f1f), where danger measures 4.38:1
//           under BidSheet's below-floor label. Info takes 400 (#60a5fa) at
//           7.25:1 on the card, 5.63:1 on its own tint and 6.48:1 on #1f1f1f;
//           danger takes 400 (#f87171) at 6.66:1 on the card and 5.96:1 on
//           #1f1f1f, and it improves the FILL pairing it already had, from
//           4.91:1 to 6.68:1 under `dangerContrast`.
//   light — warn, danger and info all go further down their own family, because
//           the published stop clears a white-ink FILL and then fails the same
//           hue used as TEXT — which is how BidSheet reads both. #d97706 is
//           3.19:1 under white; #dc2626 is 4.83:1 as a fill but 4.28:1 as ink on
//           BidSheet's danger tint. Amber lands at 800 (#92400e) rather than
//           700, because 700 is 4.43:1 on its own 6% tint over the page ground
//           and the tint would have to shrink to near-invisible to rescue it.
//           Red lands at 700 (#b91c1c). Blue lands at 700 (#1d4ed8) rather than
//           600, for the amber reason exactly: 600 is 5.17:1 under white and
//           then 4.13:1 as ink on its own 16% tint, which is what BidCard's
//           proxy chip and its `mark.liked` badge both are. 700 is 6.70:1
//           and 5.22:1 in those two roles.
//           All three clear every role they appear in; the catalog's axe gate is
//           what found each one, and it found the blue only once it started
//           rendering both schemes.
//           Success is `{primary.600}` in the design system, which is 2.56:1;
//           it takes `{primary.800}` (#0f7551, 5.70:1) — still the brand ramp,
//           which is what "success is brand-aligned" was asking for.
const STATUS = {
  light: {
    success: '#0f7551',
    successContrast: '#ffffff',
    warning: '#92400e',
    warningContrast: '#ffffff',
    danger: '#b91c1c',
    dangerContrast: '#ffffff',
    info: '#1d4ed8',
    infoContrast: '#ffffff',
  },
  dark: {
    success: '#38d898',
    successContrast: '#063c2a',
    warning: '#f59e0b',
    warningContrast: '#1a1205',
    danger: '#f87171',
    dangerContrast: '#2a0808',
    info: '#60a5fa',
    infoContrast: '#05142b',
  },
};

// The live-now badge trio, consumed by `extend.colorScheme.*.liveBadge` (see the
// note at that key for the full reasoning). Scheme-split because the plate and
// dot invert between schemes: light is a red plate with a white dot; dark is a
// neutral dark plate with a red dot. The label is white on both. The dark dot
// is `{status.danger}` so the live signal tracks the danger hue; everything
// else is fixed so white ink keeps clearing AA on whichever plate it lands on.
const LIVE_BADGE = {
  light: { background: '#c62828', dot: '#ffffff', color: '#ffffff' },
  dark: { background: '#232a2f', dot: '{status.danger}', color: '#ffffff' },
};

// The auction OUTCOME palette — the three hues the clerk console's action
// cluster needs that `STATUS` above cannot express.
//
// Deliberately separate from `status`, on the same argument that separates
// `status` from `auctionLight`: a status is a UI state (paused, failed,
// informational) while an outcome is what the auctioneer DECIDED about a lot,
// and the clerk's muscle memory is keyed to the color. No Sale is yellow and
// Floor is amber on the legacy console, and collapsing both onto
// `status.warning` would make two different verbs the same button.
//
// Only the three with no semantic equivalent live here. Sell reads
// `{primary.color}`, Floor `{status.warning}` and Undo `{status.info}`, because
// those genuinely ARE the brand accent and the warning/info signals.
//
// Per-scheme literals rather than references, for the reason `status` is: these
// hues are tuned against their own background — the dark values are saturated
// enough to carry on near-black, the light ones darkened enough to hold
// contrast against white ink.
const OUTCOME = {
  light: {
    noSale: '#a16207',
    noSaleContrast: '#ffffff',
    offer: '#0f766e',
    offerContrast: '#ffffff',
    rep: '#c2410c',
    repContrast: '#ffffff',
  },
  dark: {
    noSale: '#eab308',
    noSaleContrast: '#1f1a02',
    offer: '#2dd4bf',
    offerContrast: '#053b36',
    rep: '#fb923c',
    repContrast: '#2a1205',
  },
};

// Tag severities as SOLID status fills. Aura's default is the inverse — a 16%
// translucent tint with light text — which reads as a soft informational label
// rather than a state badge. Auction state (paused, ended, sold) needs to carry
// at a glance on a busy card, so the brand flips it to a solid fill with the
// contrast color on top. `secondary` and `contrast` keep Aura's behavior.
const tagSeverities = (scheme) => ({
  primary: { background: '{primary.color}', color: '{primary.contrast.color}' },
  success: { background: scheme.success, color: scheme.successContrast },
  info: { background: scheme.info, color: scheme.infoContrast },
  warn: { background: scheme.warning, color: scheme.warningContrast },
  danger: { background: scheme.danger, color: scheme.dangerContrast },
});

const theme = definePreset(Aura, {
  // The design system's radii scale, which is one step rounder than Aura's at
  // every stop (Aura: 2/4/6/8/12; here: 4/6/8/10/14). Its own note calls this
  // out as deliberate — "Aura uses a large default radius (rounded
  // aesthetic)" — and it is the difference between the comps and stock Aura on
  // every widget that has a corner.
  //
  // `2xl` (18px) has no Aura equivalent, so it stays in `extend.radius.card`
  // below where it already lived.
  primitive: {
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: '6px',
      md: '8px',
      lg: '10px',
      xl: '14px',
    },
  },
  semantic: {
    // Mint accent ramp, anchored on --p-primary-color (#3ee0a4) at 400 — which is
    // the shade dark mode promotes to `primary.color` — and --p-primary-contrast-color
    // (#052b1a) at 950.
    primary: PRIMARY_RAMP,
    // Slightly cool near-black neutrals matching the Midnight Mint surfaces.
    // The scheme data lives in brand.mjs so non-bundler consumers (the
    // identity provider's managed-login branding derivation) can import it
    // without this file's CSS import.
    colorScheme: COLOR_SCHEMES,
  },
  // ── Brand primitives PrimeVue has no token for ────────────────────────
  // `extend` is PrimeVue's own extension point: these are emitted as real
  // `--p-*` variables alongside every built-in token, so the brand lives
  // entirely in the preset and no hand-written stylesheet has to declare
  // them. Most of these are scheme-agnostic — a radius and an auction light
  // read the same in light and dark — so they sit flat here rather than
  // under `colorScheme`, and PrimeVue emits them once under `:root`. The
  // `colorScheme` branch further down is for the ones that are not.
  extend: {
    // Type. PrimeVue has no font-family concept at all (its only font tokens
    // are per-component font sizes/weights), so the stacks live here and one
    // rule at the document root applies them for everything to inherit.
    //
    // ONE FACE ON EVERY PLATFORM, and no system font named anywhere. Both
    // stacks lead with a face this repository ships, and everything after it is
    // an emergency fallback rather than an expected outcome.
    //
    // These stacks used to lead with SF Pro and SF Mono. That reads as the
    // careful choice — an Apple device gets the face this design language was
    // drawn against — but it quietly means the design has TWO renderings, and
    // only one of them is ever tested. SF Pro is proprietary and resolves on
    // Apple hardware only, so an Android dealer, a Windows browser and the
    // Linux Chromium that captures the visual baselines all fell through to
    // `system-ui`, which is not a font: it is a request for whatever the host
    // calls its UI face, and it answers differently on every one of them.
    //
    // That is not theoretical. The baselines were blessed in the Playwright
    // container and compared on the GitHub runner — same Ubuntu, same Chromium,
    // different font packages — and an identical 115 of 224 stories disagreed
    // on every run from the day the gate landed.
    //
    // Keeping SF Pro merely relocated that fault. Every Mac in this team is
    // where the library is actually LOOKED at — Storybook, the comp-alignment
    // harness, somebody eyeballing a component — and it would have been the one
    // environment rendering something no baseline and no non-Apple user ever
    // sees. Reviewing a screenshot you cannot reproduce locally is how a pixel
    // gate stops being believed. The faces are also not metric-compatible, so
    // the two renderings wrap text differently: two layout realities, one
    // tested.
    //
    // What this costs, so nobody rediscovers it as a bug: an iPhone no longer
    // renders the system face, and Inter reads slightly wider and rounder than
    // SF Pro Text at small sizes. That was the accepted trade. It is a smaller
    // loss than it looks — the app is a Capacitor webview pinned to
    // `user-scalable=no, maximum-scale=1.0`, so it does not honor Dynamic Type
    // either way, and it is dark-locked besides.
    //
    // `display` and `body` are the same family on purpose. SF Pro split Text
    // from Display as two families; here the display treatment lives in the
    // `display` block below — weight 700, -0.02em tracking — so the split still
    // carries what it always meant.
    //
    // NOT bundled here, which is where they belong — a preset is plain data
    // that nothing copies assets for, so the app and Storybook each ship their
    // own copy of both woff2 files and their own @font-face.
    font: {
      display: 'Inter, system-ui, sans-serif',
      body: 'Inter, system-ui, sans-serif',
      mono: '"JetBrains Mono", ui-monospace, monospace',
    },
    display: {
      weight: 700,
      tracking: '-0.02em',
    },
    // `card` is the design system's `--r-2xl`, which has no Aura equivalent —
    // the scale above stops at xl. `row` is its `--r-xl`, which now DOES have
    // one, so it references that token rather than restating 14px in a second
    // place where the two could drift.
    radius: RADIUS,
    // Industry-standard dealer signals. Deliberately NOT mapped onto
    // PrimeVue's red/green/amber/blue palette — those hues are visibly
    // different, and here the exact color carries domain meaning.
    auctionLight: {
      red: '#d92f2f',
      green: '#1ea75b',
      yellow: '#e8b41a',
      blue: '#2071e8',
      white: '#f1f1f1',
    },
    // The app's standard horizontal inset. Currently read by nothing: its two
    // consumers were Toolbar and PageContainer's gutters, both since removed.
    // Kept as the declared value for a page gutter so the next one matches the
    // last, rather than picking 16px by hand again.
    layout: {
      padding: '16px',
    },
    // The look every "live now" badge in the app is drawn on — the results
    // feed's AssetCard, the dashboard tiles and rows, My Sales, My Auctions,
    // the Bid Stack chip and the run list. One token trio (`liveBadge`), shared
    // so those badges cannot drift apart the way the AssetCard chip once went
    // green. SCHEME-SPLIT on purpose, following the broadcast convention: in
    // light a solid red plate carries a white dot and white label; in dark a
    // neutral dark plate carries a RED dot — the live signal — under the same
    // white label. White ink clears AA on both plates (5.6:1 on #c62828, far
    // more on the dark plate), which a scheme-flat bright-red plate could not
    // (dark `status.danger` #f87171 is 2.77:1 under white). Because the values
    // differ per scheme they live under `colorScheme` below (see LIVE_BADGE),
    // not here as a flat token. A shared LiveBadge component would fold the
    // markup together too; this trio is the smaller step that stops the COLORS
    // from drifting.
    // Saved — the one mark every saved heart in the app draws: a white heart on
    // a red plate. Scheme-flat on purpose: `{red.600}` holds a white heart at
    // 4.8:1 in both schemes, where dark's `status.danger` (#f87171) is 2.77:1
    // under white. Every heart reads these, so no screen's saved mark can drift.
    saved: {
      background: '{red.600}',
      color: '{surface.0}',
    },
    // State signals — won/outbid, paused/finished, proxy-armed, offer status.
    // Deliberately SEPARATE from `auctionLight` above: an auction light encodes
    // a disclosure about the vehicle (red = as-is, blue = title absent), and
    // overloading those hues onto UI state both muddies the domain meaning and
    // fails contrast, since the light hues are tuned to read as small dots on a
    // dark card rather than as text. Unlike the tokens above these are NOT
    // scheme-agnostic — each scheme needs its own value to stay legible on its
    // own background — so they sit under `colorScheme`.
    // `outcome` joins it for the same reason and on the same terms — see the
    // note on OUTCOME above for why an auction outcome is not a status.
    //
    // The component tier joins it too, for the handful of component tokens that
    // genuinely differ per scheme. This is the ONLY place PrimeVue honors a
    // scheme split: `colorScheme` nested inside a component's own block is
    // flattened into `:root` and one value silently wins in both schemes. The
    // values live beside their component in componentTokens.mjs and are spread
    // in here, so a component's tokens stay in the file named for them.
    colorScheme: {
      // `ground` is the page behind the cards — the design system's `--bg-app`,
      // which it keeps one shade deeper than `--bg-card` in both schemes
      // (#f8fafc vs #ffffff in light, #0a0a0a vs #141414 in dark). PrimeVue has
      // no token for it, which is why `src/variables.css` used to forward the
      // ground to the card surface and accept that cards did not read as
      // raised. That file names this exact fix and these exact references.
      light: { ground: '{surface.50}', status: STATUS.light, outcome: OUTCOME.light, liveBadge: LIVE_BADGE.light, ...componentTokensByColorScheme.light },
      dark: { ground: '{surface.950}', status: STATUS.dark, outcome: OUTCOME.dark, liveBadge: LIVE_BADGE.dark, ...componentTokensByColorScheme.dark },
    },
    // ── The component tier ───────────────────────────────────────────────
    // PrimeVue's third token tier, for the components PrimeVue does not know
    // about: one block per component, namespaced by the package that owns it,
    // so `.sale-card` reads `--p-sale-card-background` rather than the semantic
    // token behind it and a consumer can move one component without moving
    // every component that shares its color.
    //
    // Split into its own module for length only — it is longer than the rest of
    // the brand put together, and none of it is a brand decision. The naming
    // rule, the property set and the gates are in
    // knowledge-base/standards/component-token-contract.md.
    ...componentTokens,
  },
  // ── PrimeVue widget tokens ────────────────────────────────────────────
  // Only widgets whose Aura defaults are wrong for this brand. Tag is here
  // because the app uses it as a state badge (SaleStatusBanner's `chip`
  // variant on the BidStack card), and Aura's translucent-tint default does
  // not read at that size. Sizing is deliberately NOT set here: a state badge
  // is 8px, an ordinary Tag is not, so density stays a per-instance `dt`
  // override and this block governs color only.
  components: {
    tag: {
      colorScheme: {
        light: tagSeverities(STATUS.light),
        dark: tagSeverities(STATUS.dark),
      },
    },
    // The app's menu and search panels. This replaced a hand-rolled `Drawer`
    // component, which is why these are widget overrides rather than another
    // `extend` block: the component IS PrimeVue's now.
    //
    // Aura dresses a Drawer as an overlay modal — a panel floating on a dimmed
    // page. On a phone it is the page: edge to edge, no shadow, and reading as
    // the same material as the content it slid over. Color only, as with `tag`
    // above; the panel's WIDTH is the app's layout decision and lives with the
    // drawer host, because 75%-of-viewport is a mobile choice and not something
    // every future Drawer should inherit.
    drawer: {
      root: {
        background: '{content.background}',
        borderColor: '{content.border.color}',
        color: '{text.color}',
        shadow: 'none',
      },
      // No header is rendered — the host uses Drawer's `#container` slot and
      // draws its own panel — so these only matter if a future call site opts
      // back into the default structure. Kept in step with the content padding
      // the app actually uses rather than left at Aura's 1.25rem modal inset.
      header: { padding: '0' },
      content: { padding: '0' },
      footer: { padding: '0' },
    },
  },
});

export default theme;
